/**
 * ExpertShareCard Component
 *
 * Shareable card featuring Drama Expert voice with pattern insights.
 * Designed for utility-focused sharing ("My Drama Expert said..." format).
 *
 * ## Visual Design
 * - **Theme**: Blue (#EFF6FF background, #2563EB accents)
 * - **Typography**: Inter (professional, analytical aesthetic)
 * - **Layout**: Expert insight + pattern tag + attribution
 * - **Dimensions**: 1080×1920px (9:16 Instagram Stories)
 *
 * ## Content Structure
 * ```
 * ┌────────────────────────┐
 * │  "My Drama Expert      │ (Header text)
 * │   said..."             │
 * │                        │
 * │  "Main insight text    │ (Large quote, bold)
 * │   from analysis"       │
 * │                        │
 * │  #PatternTag           │ (Colored badge)
 * │                        │
 * │  "Drama Expert by      │ (Small attribution)
 * │   DramaDetector"       │
 * └────────────────────────┘
 * ```
 *
 * ## Usage
 * ```tsx
 * <ExpertShareCard
 *   insightText="Your tone shifts from collaborative to defensive when deadlines are mentioned."
 *   patternTag="Defensive Language"
 *   dramaScore={65}
 * />
 * ```
 *
 * Created: 2025-01-11 (Task 2.2: Viral Share Formats)
 */

import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { useTranslation } from "@dramadetector/shared";

/**
 * Props for ExpertShareCard component
 */
export interface ExpertShareCardProps {
	/**
	 * Main insight text from Drama Expert analysis
	 * Should be concise (1-2 sentences max) for readability
	 * Example: "Your tone shifts from collaborative to defensive when deadlines are mentioned."
	 */
	insightText: string;

	/**
	 * Pattern tag (communication pattern category)
	 * Example: "Defensive Language", "Passive Aggressive", "Emotional Escalation"
	 */
	patternTag: string;

	/**
	 * Drama score (0-10 scale) for color coding
	 * Used to determine pattern badge color
	 */
	dramaScore: number;

	/**
	 * Optional test ID for E2E testing
	 * @default 'expert-share-card'
	 */
	testID?: string;
}

/**
 * Convert drama score (0-10 scale) to badge color
 */
function getPatternBadgeColor(score: number): string {
	if (score <= 2) return "#10B981"; // Green (low risk)
	if (score <= 5) return "#F59E0B"; // Yellow (moderate risk)
	return "#EF4444"; // Red (high risk)
}

export function ExpertShareCard({
	insightText,
	patternTag,
	dramaScore,
	testID = "expert-share-card",
}: ExpertShareCardProps) {
	const { t } = useTranslation("share", { useSuspense: false }) as {
		t: (key: string, options?: any) => string;
	};
	const badgeColor = getPatternBadgeColor(dramaScore);

	return (
		<View style={styles.container} testID={testID}>
			{/* Header Text */}
			<Text style={styles.headerText} testID={`${testID}-header`}>
				{t("share_card.expert.header")}
			</Text>

			{/* Main Insight Quote */}
			<View style={styles.quoteContainer}>
				<Text style={styles.quoteText} testID={`${testID}-insight`}>
					{'"'}
					{insightText}
					{'"'}
				</Text>
			</View>

			{/* Pattern Tag Badge */}
			<View
				style={[styles.patternBadge, { backgroundColor: badgeColor }]}
				testID={`${testID}-pattern`}
			>
				<Text style={styles.patternText}>
					#{patternTag.replace(/\s+/g, "")}
				</Text>
			</View>

			{/* Expert Attribution */}
			<Text style={styles.attribution} testID={`${testID}-attribution`}>
				{t("share_card.expert.attribution")}
			</Text>

			{/* Decorative Analysis Icon (visual flair) */}
			<View style={styles.decorativeIcon}>
				<Text style={styles.iconText}>📊</Text>
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		paddingHorizontal: 48,
		paddingVertical: 60,
	},
	headerText: {
		fontSize: 28,
		fontWeight: "600",
		color: "#1E40AF", // Blue-800
		textAlign: "center",
		marginBottom: 32,
		fontFamily: "Inter",
	},
	quoteContainer: {
		backgroundColor: "#DBEAFE", // Blue-100 (subtle highlight)
		borderLeftWidth: 6,
		borderLeftColor: "#2563EB", // Blue-600
		paddingHorizontal: 32,
		paddingVertical: 40,
		marginBottom: 40,
		borderRadius: 8,
	},
	quoteText: {
		fontSize: 36,
		fontWeight: "700",
		color: "#1E3A8A", // Blue-900
		textAlign: "left",
		lineHeight: 48,
		fontFamily: "Inter",
	},
	patternBadge: {
		paddingHorizontal: 28,
		paddingVertical: 14,
		borderRadius: 28,
		marginBottom: 48,
	},
	patternText: {
		fontSize: 20,
		fontWeight: "700",
		color: "#FFFFFF",
		fontFamily: "Inter",
	},
	attribution: {
		fontSize: 16,
		fontWeight: "500",
		color: "#60A5FA", // Blue-400
		textAlign: "center",
		marginTop: 32,
		fontFamily: "Inter",
	},
	decorativeIcon: {
		position: "absolute",
		top: 30,
		right: 40,
		opacity: 0.2,
	},
	iconText: {
		fontSize: 100,
	},
});
